import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import {
  companyEventApiClient,
  EventIndexResponseData,
} from '@src/api/company/event/eventApiClient.ts';
import { mediaCategoryApiClient } from '@src/api/company/mediaCategory/mediaCategoryApiClient.ts';
import { mediaTagApiClient } from '@src/api/company/mediaTag/mediaTagApiClient.ts';
import { MediaCategory } from '@src/api/types/mediaCategory.ts';
import { MediaTag } from '@src/api/types/mediaTag.ts';
import { Pagination } from '@src/api/types/pagination.ts';
import PaginationComponent from '@src/components/Pagination.tsx';
import { useEventMenu } from '@src/context/EventMenuContext';
import { EventProgress } from '@src/enum/EventProgress.ts';
import { RECEPTION_STATUS } from '@src/enum/receptionStatus.ts';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import { COMPANY_URL } from '@src/routes/company';
import DropdownMenu from '@src/ui/elements/form/DropdownMenu';
import InputSearch from '@src/ui/elements/form/InputSearch';
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import EventsItem from './components/EventsItem';

const FORM_CONSTANTS = {
  INITIAL_TITLE: 'すべてのイベント',
  PER_PAGE: 6,
  INTEGER_RADIX: 10,
} as const;

const DEFAULT_SELECTED_OPTION = {
  label: 'すべて',
  value: '',
};

function EventList() {
  const [events, setEvents] = useState<EventIndexResponseData[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [mediaCategories, setMediaCategories] = useState<MediaCategory[]>([]);
  const [mediaTags, setMediaTags] = useState<MediaTag[]>([]);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [searchedKeyword, setSearchedKeyword] = useState<string>('');
  const [caption, setCaption] = useState<string>('');
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [selectedReceptionStatusOption, setSelectedReceptionStatusOption] = useState<{
    label: string;
    value: string;
  }>(DEFAULT_SELECTED_OPTION);
  const [isPastEvent, setIsPastEvent] = useState(false);

  const location = useLocation();
  const navigationType = useNavigationType();

  const { eventMenus } = useEventMenu();

  const loadEvents = async (
    page = 1,
    keyword: string | null = null,
    category: MediaCategory | null = null,
    tag: MediaTag | null = null,
    eventProgress: string | null = null,
    receptionStatus: string | null = null,
  ) => {
    const params: {
      page: number;
      keyword?: string;
      per_page: number;
      category_slug?: string;
      tag_slug?: string;
      event_progress?: string;
      reception_status?: number;
    } = {
      page,
      per_page: FORM_CONSTANTS.PER_PAGE,
    };
    if (category && category.slug) {
      params.category_slug = category.slug;
    } else if (tag && tag.slug) {
      params.tag_slug = tag.slug;
    } else if (keyword) {
      params.keyword = keyword;
    }
    if (eventProgress) {
      params.event_progress = eventProgress;
    }
    if (receptionStatus) {
      params.reception_status = Number(receptionStatus);
    }
    const res = await companyEventApiClient.index(params);
    handleApiResponse(res, (data) => {
      setEvents(data.data);
      setPagination(data.pagination);
      setSearchValue(params.keyword || '');
      setSearchedKeyword(params.keyword || '');
      setCaption(category?.name || tag?.name || keyword || '');
    });
  };

  useEffect(() => {
    const loadMediaCategories = async () => {
      setCategoriesLoaded(false);
      const res = await mediaCategoryApiClient.index();
      handleApiResponse(res, (data) => {
        setMediaCategories(data);
      });
      setCategoriesLoaded(true);
    };

    loadMediaCategories();
  }, []);

  useEffect(() => {
    const loadMediaTags = async () => {
      setTagsLoaded(false);
      const res = await mediaTagApiClient.index();
      handleApiResponse(res, (data) => {
        setMediaTags(data);
      });
      setTagsLoaded(true);
    };

    loadMediaTags();
  }, []);

  useEffect(() => {
    const state = location.state as {
      fromCategoryClick?: boolean;
      fromTagClick?: boolean;
      previousUrl?: string;
    } | null;

    const isBackNavigation = navigationType === 'POP';

    if (isBackNavigation && (state?.fromCategoryClick || state?.fromTagClick)) {
      navigate(state?.previousUrl || COMPANY_URL.EVENTS);
    }
  }, [location, navigationType, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!categoriesLoaded || !tagsLoaded) {
        return;
      }

      const query = new URLSearchParams(location.search);
      const page = parseInt(query.get('page') || '1', FORM_CONSTANTS.INTEGER_RADIX);
      const keyword = query.get('keyword');
      const categorySlug = query.get('category_slug') || '';
      const category = mediaCategories.find((c) => c.slug === categorySlug) || null;
      const tagSlug = query.get('tag_slug') || '';
      const tag = mediaTags.find((t) => t.slug === tagSlug) || { id: 0, name: '' };
      const eventProgress = query.get('event_progress');
      const receptionStatus = query.get('reception_status');
      await loadEvents(page, keyword, category, tag, eventProgress, receptionStatus);
      window.scrollTo(0, 0);
    };

    fetchData();
  }, [location.search, categoriesLoaded, tagsLoaded]);

  useEffect(() => {
    const getCurrentEventMenu = async () => {
      const query = new URLSearchParams(location.search);
      const eventProgress = query.get('event_progress');
      if (eventProgress && eventProgress.trim() === EventProgress.PAST_EVENTS) {
        setIsPastEvent(true);
      } else {
        setIsPastEvent(false);
      }
    };

    getCurrentEventMenu();
  }, [location.search, eventMenus]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const receptionStatus = query.get('reception_status') || '';
    const updatedReceptionStatusOption = receptionStatusOptions.find(
      (opt) => opt.value === receptionStatus,
    );
    if (updatedReceptionStatusOption) {
      setSelectedReceptionStatusOption(updatedReceptionStatusOption);
    }
  }, [location.search]);

  const handlePageChange = (page: number) => {
    const query = new URLSearchParams(location.search);
    if (query.get('page') === page.toString()) {
      return;
    }

    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleSearchSubmit = () => {
    const query = new URLSearchParams(location.search);
    const newKeyword = searchValue.trim();

    query.set('keyword', newKeyword);
    query.set('page', '1');
    query.delete('category_slug');
    query.delete('tag_slug');
    setCaption(newKeyword);
    navigate({ search: query.toString() });
  };

  const handleCaption = (caption: string) => {
    const filteredCategory = mediaCategories.find((c) => c.name === caption);
    const filteredTag = mediaTags.find((t) => t.name === caption);

    if (filteredCategory || filteredTag) {
      return caption;
    }
    if (events.length > 0) {
      if (!caption) {
        return FORM_CONSTANTS.INITIAL_TITLE;
      }
      return `${caption}の検索結果`;
    }
    if (!searchedKeyword && !filteredCategory && !filteredTag) {
      return '';
    }
    return `${caption}の検索結果：0件`;
  };

  const handleReceptionStatusSelect = (option: { label: string; value: string }) => {
    const query = new URLSearchParams(location.search);
    if (query.get('reception_status') === option.value) {
      return;
    }
    const updatedReceptionStatusOption = receptionStatusOptions.find(
      (opt) => opt.value === option.value,
    );
    if (!updatedReceptionStatusOption) {
      return;
    }

    setSelectedReceptionStatusOption(updatedReceptionStatusOption);
    query.set('reception_status', option.value);
    query.set('page', '1');
    navigate({ search: query.toString() });
  };

  const receptionStatusOptions = [
    {
      ...DEFAULT_SELECTED_OPTION,
    },
    {
      label: '受付中',
      value: `${RECEPTION_STATUS.RECRUITING}`,
    },
    {
      label: '受付開始前',
      value: `${RECEPTION_STATUS.BEFORE_RECRUITING}`,
    },
    {
      label: '受付終了',
      value: `${RECEPTION_STATUS.CLOSED}`,
    },
  ];

  const { renderScrollMobile } = useScrollMobile({
    items: eventMenus.map((item) => ({
      to: item.link || '',
      label: item.title,
      isCurrent: item.isCurrent,
    })),
  });

  return (
    <main className='px-6 pb-16 pt-10 lg:px-6 md:pt-6'>
      {/* ヘッダー部分 */}
      <div className='flex items-center justify-between lg:flex-col lg:items-start'>
        <h1 className='overflow-wrap-anywhere text-xl font-semibold lg:mt-6'>
          {handleCaption(caption)}
        </h1>
        <div className='mt-4 hidden max-w-full lg:-order-1 lg:block'>
          <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
        </div>
        <div className='w-[250px] min-w-[250px] lg:-order-2 lg:w-full'>
          <InputSearch
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onSubmit={handleSearchSubmit}
            onClear={() => {
              setSearchValue('');
              navigate({ search: '' });
            }}
          />
        </div>
      </div>
      {!isPastEvent && (
        <div className='mt-2 w-[160px]'>
          <DropdownMenu
            containerClassName='z-10'
            variant='primary'
            options={receptionStatusOptions}
            defaultValue={selectedReceptionStatusOption}
            onSelect={handleReceptionStatusSelect}
          />
        </div>
      )}
      {/* イベント一覧 */}
      <div className='mt-8 grid grid-cols-2 gap-10 md:mt-6 md:grid-cols-1'>
        {events.map((event, index) => (
          <EventsItem
            key={index}
            event={event}
          />
        ))}
      </div>

      {/* ページング */}
      {pagination && pagination.total > FORM_CONSTANTS.PER_PAGE && (
        <div className='mt-16 lg:flex lg:justify-center'>
          <PaginationComponent
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </main>
  );
}

export default EventList;
